import React, { useEffect, useState } from 'react';
import {
  collection, getDocs, addDoc, updateDoc, doc, serverTimestamp, query, orderBy, deleteDoc
} from 'firebase/firestore';
import { db } from './firebase';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  startOfWeek, endOfWeek, isSameMonth, isSameDay, isToday,
  addMonths, subMonths,
} from 'date-fns';
import { es } from 'date-fns/locale';

const TIPOS_EXP = ['Experiencia de velas', 'Taller aromas', 'Evento privado', 'Otro'];

export function Calendario({ setActive }) {
  const [mes, setMes] = useState(new Date());
  const [experiencias, setExperiencias] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    const snap = await getDocs(query(collection(db, 'experiencias'), orderBy('fecha', 'asc')));
    const rows = [];
    snap.forEach(d => rows.push({ id: d.id, ...d.data() }));
    setExperiencias(rows);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  const start = startOfWeek(startOfMonth(mes), { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(mes), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start, end });

  const expForDay = (day) => experiencias.filter(e => {
    const fecha = e.fecha?.toDate ? e.fecha.toDate() : new Date(e.fecha);
    return isSameDay(fecha, day);
  });

  const dayExp = selectedDay ? expForDay(selectedDay) : [];

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <div className="page-header">
        <h1>Calendario</h1>
        <p>Organización de experiencias y eventos</p>
      </div>
      <div className="page-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setMes(m => subMonths(m, 1))}>← Anterior</button>
          <h2 style={{ fontSize: 24, textTransform: 'capitalize', fontFamily: 'var(--font-display)' }}>
            {format(mes, 'MMMM yyyy', { locale: es })}
          </h2>
          <button className="btn btn-ghost btn-sm" onClick={() => setMes(m => addMonths(m, 1))}>Siguiente →</button>
        </div>

        <div className="calendar-grid">
          {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
            <div key={d} className="cal-header">{d}</div>
          ))}
          {days.map(day => (
            <div
              key={day.toString()}
              className={`cal-day${isToday(day) ? ' today' : ''}${!isSameMonth(day, mes) ? ' other-month' : ''}`}
              onClick={() => setSelectedDay(day)}
              style={{ cursor: 'pointer', borderColor: selectedDay && isSameDay(day, selectedDay) ? 'var(--olive-light)' : undefined }}
            >
              <div className="cal-day-num">{format(day, 'd')}</div>
              {expForDay(day).map(e => (
                <div key={e.id} className="cal-event" title={e.nombre}>{e.nombre}</div>
              ))}
            </div>
          ))}
        </div>

        {selectedDay && (
          <div className="card" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="card-title" style={{ marginBottom: 0 }}>
                {format(selectedDay, "EEEE d 'de' MMMM", { locale: es })}
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => setActive('experiencias')}>
                + Nueva experiencia
              </button>
            </div>
            {dayExp.length === 0 ? (
              <p style={{ color: 'var(--gray-light)', fontSize: 13 }}>Sin experiencias este día.</p>
            ) : dayExp.map(e => (
              <div key={e.id} className="alert-item alert-info" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                  <strong>{e.nombre}</strong>
                  <span className="badge badge-olive">{e.tipo}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-light)', marginTop: 4 }}>
                  {format(e.fecha?.toDate ? e.fecha.toDate() : new Date(e.fecha), 'HH:mm')} · {e.lugar || 'Sin lugar'}
                </div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  {e.participantes?.length || 0} participantes · Ingreso: ${(e.ingresos || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function Experiencias() {
  const [experiencias, setExperiencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const emptyForm = {
    nombre: '', tipo: 'Experiencia de velas', fecha: '', hora: '19:00',
    lugar: '', descripcion: '', capacidad: '', precio: '',
    participantes: [], ingresos: ''
  };
  const [form, setForm] = useState(emptyForm);
  const [newParticipante, setNewParticipante] = useState({ nombre: '', email: '', telefono: '', pagado: false });

  async function load() {
    setLoading(true);
    const snap = await getDocs(query(collection(db, 'experiencias'), orderBy('fecha', 'desc')));
    const rows = [];
    snap.forEach(d => rows.push({ id: d.id, ...d.data() }));
    setExperiencias(rows);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  function openEdit(exp) {
    const fecha = exp.fecha?.toDate ? exp.fecha.toDate() : new Date(exp.fecha);
    setForm({
      ...emptyForm,
      ...exp,
      fecha: format(fecha, 'yyyy-MM-dd'),
      hora: format(fecha, 'HH:mm'),
      participantes: exp.participantes || []
    });
    setEditItem(exp);
    setShowModal(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const fechaISO = new Date(`${form.fecha}T${form.hora}`);
      const data = {
        nombre: form.nombre,
        tipo: form.tipo,
        fecha: fechaISO,
        lugar: form.lugar,
        descripcion: form.descripcion,
        capacidad: Number(form.capacidad) || 0,
        precio: Number(form.precio) || 0,
        participantes: form.participantes || [],
        ingresos: Number(form.ingresos) || 0,
        updatedAt: serverTimestamp()
      };
      if (editItem) {
        await updateDoc(doc(db, 'experiencias', editItem.id), data);
      } else {
        await addDoc(collection(db, 'experiencias'), { ...data, createdAt: serverTimestamp() });
      }
      await load();
      setShowModal(false);
      setEditItem(null);
      setForm(emptyForm);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('¿Eliminar esta experiencia?')) return;
    await deleteDoc(doc(db, 'experiencias', id));
    await load();
  }

  function addParticipante() {
    if (!newParticipante.nombre.trim()) return;
    setForm(f => ({ ...f, participantes: [...(f.participantes || []), { ...newParticipante, id: Date.now() }] }));
    setNewParticipante({ nombre: '', email: '', telefono: '', pagado: false });
  }

  function removeParticipante(id) {
    setForm(f => ({ ...f, participantes: f.participantes.filter(p => p.id !== id) }));
  }

  function togglePagado(id) {
    setForm(f => ({
      ...f,
      participantes: f.participantes.map(p => p.id === id ? { ...p, pagado: !p.pagado } : p)
    }));
  }

  return (
    <div>
      <div className="page-header">
        <h1>Experiencias</h1>
        <p>Gestión de eventos y talleres de velas</p>
      </div>
      <div className="page-body">
        <div className="toolbar">
          <div />
          <button className="btn btn-primary" onClick={() => { setShowModal(true); setEditItem(null); setForm(emptyForm); setError(''); }}>
            + Nueva experiencia
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loading ? <div className="spinner" /> : experiencias.length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">◆</div><p>Sin experiencias registradas</p></div>
          ) : experiencias.map(exp => {
            const fecha = exp.fecha?.toDate ? exp.fecha.toDate() : new Date(exp.fecha);
            const isPast = fecha < new Date();
            return (
              <div key={exp.id} className="card" style={{ opacity: isPast ? 0.75 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontFamily: 'var(--font-display)', fontSize: 20 }}>{exp.nombre}</span>
                      <span className="badge badge-olive">{exp.tipo}</span>
                      {isPast && <span className="badge badge-gray">Pasada</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--gray-light)' }}>
                      {format(fecha, "EEEE d 'de' MMMM · HH:mm", { locale: es })} · {exp.lugar || 'Sin lugar'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(exp)}>Editar</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(exp.id)}>Eliminar</button>
                  </div>
                </div>
                <hr className="divider" style={{ margin: '12px 0' }} />
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
                  <div>
                    <span style={{ color: 'var(--gray-light)' }}>Participantes: </span>
                    <strong>{exp.participantes?.length || 0}{exp.capacidad ? ` / ${exp.capacidad}` : ''}</strong>
                  </div>
                  <div>
                    <span style={{ color: 'var(--gray-light)' }}>Precio: </span>
                    <strong>${(exp.precio || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</strong>
                  </div>
                  <div>
                    <span style={{ color: 'var(--gray-light)' }}>Ingresos: </span>
                    <strong style={{ color: 'var(--olive-light)' }}>${(exp.ingresos || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</strong>
                  </div>
                  <div>
                    <span style={{ color: 'var(--gray-light)' }}>Pagaron: </span>
                    <strong>{exp.participantes?.filter(p => p.pagado).length || 0}</strong>
                  </div>
                </div>
                {exp.participantes?.length > 0 && (
                  <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {exp.participantes.map(p => (
                      <span key={p.id} className={`badge ${p.pagado ? 'badge-green' : 'badge-gray'}`} title={p.email}>
                        {p.nombre} {p.pagado ? '✓' : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{editItem ? 'Editar experiencia' : 'Nueva experiencia'}</div>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
            <form className="modal-form" onSubmit={handleSubmit}>
              <div className="field-group">
                <label>Nombre del evento</label>
                <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} required placeholder="Ej: Taller de velas aromáticas" />
              </div>
              <div className="grid-2">
                <div className="field-group">
                  <label>Tipo</label>
                  <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                    {TIPOS_EXP.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="field-group">
                  <label>Lugar</label>
                  <input value={form.lugar} onChange={e => setForm(f => ({ ...f, lugar: e.target.value }))} placeholder="Dirección o espacio" />
                </div>
              </div>
              <div className="grid-2">
                <div className="field-group">
                  <label>Fecha</label>
                  <input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} required />
                </div>
                <div className="field-group">
                  <label>Hora</label>
                  <input type="time" value={form.hora} onChange={e => setForm(f => ({ ...f, hora: e.target.value }))} required />
                </div>
              </div>
              <div className="grid-2">
                <div className="field-group">
                  <label>Capacidad</label>
                  <input type="number" min="0" value={form.capacidad} onChange={e => setForm(f => ({ ...f, capacidad: e.target.value }))} />
                </div>
                <div className="field-group">
                  <label>Precio por persona $</label>
                  <input type="number" min="0" value={form.precio} onChange={e => setForm(f => ({ ...f, precio: e.target.value }))} />
                </div>
              </div>
              <div className="field-group">
                <label>Ingresos totales $</label>
                <input type="number" min="0" value={form.ingresos} onChange={e => setForm(f => ({ ...f, ingresos: e.target.value }))} />
              </div>
              <div className="field-group">
                <label>Descripción / notas</label>
                <textarea rows={2} value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                  style={{ background: 'var(--gray)', border: '1px solid var(--gray-mid)', color: 'var(--white)', padding: '10px 12px', borderRadius: 'var(--radius)', resize: 'vertical' }} />
              </div>

              <hr className="divider" />
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, letterSpacing: '0.05em', marginBottom: 8 }}>Participantes</div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div className="field-group" style={{ flex: 1, minWidth: 100 }}>
                  <label>Nombre</label>
                  <input value={newParticipante.nombre} onChange={e => setNewParticipante(p => ({ ...p, nombre: e.target.value }))} placeholder="Nombre" />
                </div>
                <div className="field-group" style={{ flex: 1, minWidth: 100 }}>
                  <label>Email</label>
                  <input value={newParticipante.email} onChange={e => setNewParticipante(p => ({ ...p, email: e.target.value }))} placeholder="Email" />
                </div>
                <div className="field-group" style={{ flex: 1, minWidth: 80 }}>
                  <label>Teléfono</label>
                  <input value={newParticipante.telefono} onChange={e => setNewParticipante(p => ({ ...p, telefono: e.target.value }))} placeholder="Tel" />
                </div>
                <button type="button" className="btn btn-secondary btn-sm" style={{ marginBottom: 0 }} onClick={addParticipante}>+</button>
              </div>

              {(form.participantes || []).length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {form.participantes.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--gray-mid)', fontSize: 13 }}>
                      <span style={{ flex: 1 }}>{p.nombre}</span>
                      <span style={{ color: 'var(--gray-light)' }}>{p.email}</span>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 11 }}>
                        <input type="checkbox" checked={p.pagado} onChange={() => togglePagado(p.id)} />
                        Pagó
                      </label>
                      <button type="button" style={{ background: 'none', border: 'none', color: 'var(--gray-light)', cursor: 'pointer', fontSize: 14 }} onClick={() => removeParticipante(p.id)}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '...' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}